import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { cn } from "@/shared/lib/utils";

/**
 * Radix Select wrapper. Trigger matches the Input primitive's surface
 * (glass-surface-subtle, rounded-lg, px-4, h-10). Content uses the
 * mid-tier glass for the floating panel per design.md §8 + §9.
 */
export interface SelectOption {
  value: string;
  label: ReactNode;
}

export interface SelectInputProps
  extends Omit<ComponentPropsWithoutRef<typeof SelectPrimitive.Root>, "children"> {
  options: SelectOption[];
  placeholder?: string;
  /** Forwarded to the trigger button. */
  className?: string;
  /** Forwarded to the trigger button. */
  id?: string;
  /** Forwarded to the trigger button. */
  "aria-invalid"?: boolean;
  /** Forwarded to the trigger button. */
  "aria-describedby"?: string;
  /** Forwarded to the trigger button (for selects without a visible label). */
  "aria-label"?: string;
}

export const SelectInput = forwardRef<HTMLButtonElement, SelectInputProps>(
  function SelectInput(
    { options, placeholder, className, id, "aria-invalid": ariaInvalid, "aria-describedby": ariaDescribedBy, "aria-label": ariaLabel, ...rootProps },
    ref,
  ) {
    return (
      <SelectPrimitive.Root {...rootProps}>
        <SelectPrimitive.Trigger
          ref={ref}
          id={id}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          aria-label={ariaLabel}
          className={cn(
            "inline-flex h-10 w-full items-center justify-between gap-2",
            "px-4 type-body-base",
            "rounded-lg border border-border",
            "glass-surface-subtle text-text",
            "data-[placeholder]:text-text-muted",
            "focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            className,
          )}
        >
          <SelectPrimitive.Value placeholder={placeholder} />
          <SelectPrimitive.Icon asChild>
            <ChevronDown className="h-4 w-4 text-text-muted" aria-hidden />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            className={cn(
              "z-dropdown overflow-hidden",
              "rounded-xl glass-surface",
              "shadow-lg",
              // Match the floating panel to the trigger's width.
              "w-[var(--radix-select-trigger-width)]",
            )}
            position="popper"
            sideOffset={4}
          >
            <SelectPrimitive.Viewport className="p-1">
              {options.map((opt) => (
                <SelectPrimitive.Item
                  key={opt.value}
                  value={opt.value}
                  className={cn(
                    "relative flex select-none items-center gap-2",
                    // Left gutter (pl-8) reserves room for the check so it
                    // never overlaps the label.
                    "rounded-lg py-2 pl-8 pr-3 type-body-base text-text",
                    "outline-none cursor-pointer",
                    "data-[highlighted]:bg-surface-muted/40",
                    "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed",
                  )}
                >
                  <SelectPrimitive.ItemIndicator className="absolute left-2 inline-flex h-4 w-4 items-center justify-center">
                    <Check className="h-3 w-3 text-brand" aria-hidden />
                  </SelectPrimitive.ItemIndicator>
                  <SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    );
  },
);
